import { useCallback, useEffect, useRef, useState } from "react";
import { EngagementSurveyReportGroupBy } from "types/graphql-schema";

import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import Checkbox from "@components/checkbox";
import { Select, SelectOption } from "@components/select/select";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { classNames } from "@helpers/css";

import { useGroupByOptions } from "./helpers";
import { EngagmentSurveyGroupBySelectorItem } from "./types";

type GroupBySelectorProps<T extends EngagementSurveyReportGroupBy | null> = {
  groupBy: T;
  onChangeGroupBy: (groupBy: T) => void;
  /** Title for the group by section */
  title?: string;
  /** Whether to include "None" option in the dropdown */
  includeNoneOption: T extends null ? true : false;
  /** Items that can be selected/deselected */
  selectableItems: EngagmentSurveyGroupBySelectorItem[];
  /** Keys of items that are currently hidden */
  hiddenItemKeys: string[];
  /** Called when hidden items change */
  onChangeHiddenItems?: (hiddenKeys: string[]) => void;
  /** Info message to show above the checkboxes */
  infoMessage?: string;
  /** Minimum number of items to show checkboxes (default: 2) */
  minItemsForCheckboxes?: number;
  /** Group by values that should not show checkboxes */
  hideCheckboxesForGroupBy?: EngagementSurveyReportGroupBy[];
  /** Whether the data is currently loading */
  isLoading?: boolean;
};

const EngagementSurveyGroupBySelector = <
  T extends EngagementSurveyReportGroupBy | null,
>({
  groupBy,
  onChangeGroupBy,
  title = "Group By",
  includeNoneOption,
  selectableItems,
  hiddenItemKeys = [],
  onChangeHiddenItems,
  infoMessage,
  minItemsForCheckboxes = 2,
  hideCheckboxesForGroupBy = [EngagementSurveyReportGroupBy.IsManager],
  isLoading = false,
}: GroupBySelectorProps<T>) => {
  const debouncedId = useRef<NodeJS.Timeout | null>(null);
  const [proposedHiddenItems, setProposedHiddenItems] =
    useState(hiddenItemKeys);

  useEffect(() => {
    setProposedHiddenItems(hiddenItemKeys);
  }, [hiddenItemKeys]);

  useEffect(() => {
    return () => {
      if (debouncedId.current) {
        clearTimeout(debouncedId.current);
      }
    };
  }, []);

  const groupByOptions = useGroupByOptions<T>(includeNoneOption);

  const handleChangeGroupBy = useCallback(
    (opt: SelectOption<T>) => {
      onChangeGroupBy(opt.value);
    },
    [onChangeGroupBy],
  );

  const handleToggleItem = useCallback(
    (itemKey: string) => {
      const isCurrentlyHidden = proposedHiddenItems.includes(itemKey);
      const newHiddenItems = isCurrentlyHidden
        ? proposedHiddenItems.filter((key) => key !== itemKey) // Was hidden, now show it (remove from hidden list)
        : [...proposedHiddenItems, itemKey]; // Was visible, now hide it (add to hidden list)
      setProposedHiddenItems(newHiddenItems);

      const oldTimeout = debouncedId.current;
      if (oldTimeout) {
        clearTimeout(oldTimeout);
      }
      debouncedId.current = setTimeout(() => {
        onChangeHiddenItems?.(newHiddenItems);
      }, 800);
    },
    [proposedHiddenItems, onChangeHiddenItems],
  );

  const handleSelectAll = useCallback(() => {
    onChangeHiddenItems?.([]);
  }, [onChangeHiddenItems]);

  const handleDeselectAll = useCallback(() => {
    if (selectableItems) {
      const allKeys = selectableItems.map((item) => item.key);
      onChangeHiddenItems?.(allKeys);
    }
  }, [selectableItems, onChangeHiddenItems]);

  const showCheckboxes =
    selectableItems &&
    selectableItems.length >= minItemsForCheckboxes &&
    groupBy &&
    !hideCheckboxesForGroupBy.includes(groupBy);

  return (
    <div className="border-t p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="font-semibold">{title}</div>

        {showCheckboxes && (
          <div className="flex gap-2">
            <Button
              text="Select All"
              theme={buttonTheme.lightBlue}
              className="bg-transparent font-medium"
              onClick={handleSelectAll}
            />
            <Button
              text="Deselect All"
              theme={buttonTheme.lightBlue}
              className="bg-transparent font-medium"
              onClick={handleDeselectAll}
            />
          </div>
        )}
      </div>
      <div className="mb-6">
        <Select
          options={groupByOptions}
          value={groupBy}
          onChange={handleChangeGroupBy}
        />
      </div>
      {showCheckboxes && (
        <>
          {infoMessage && (
            <div className="mb-6 p-4 border rounded-lg flex gap-3 bg-gray-50 border-gray-100 text-gray-600 text-sm">
              <TopicflowIcon type="info" className="mt-0.5 h-4 w-4" />
              <div>{infoMessage}</div>
            </div>
          )}
          <div
            className={classNames(
              "flex flex-col gap-2 text-gray-600",
              isLoading && "opacity-40",
            )}
          >
            {selectableItems.map((item) => {
              const isChecked = !proposedHiddenItems.includes(item.key);

              return (
                <Checkbox
                  key={item.key}
                  disabled={isLoading}
                  label={item.label}
                  checked={isChecked}
                  onChange={() => handleToggleItem(item.key)}
                />
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default EngagementSurveyGroupBySelector;
